import { Component, inject, signal, OnInit, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';

import { PaymentService } from '../../../services/payment';
import { AuthService } from '../../../services/auth';
import { PageTitleService } from '../../../services/page-title';

/**
 * Where a REDIRECT gateway drops the customer back.
 *
 * Unlike Razorpay's popup, a redirect gateway takes the browser away and returns it here, so the
 * page cannot assume the tab that started the payment is still around holding any state. It gets
 * only `?ref=` and reads the truth from the server.
 *
 * The status in the query string is a HINT, never the answer. The payment is re-read (and the
 * gateway re-checked) so a customer who paid but whose return was mangled still lands on
 * "successful" — and, just as importantly, a tampered `?status=SUCCESS` cannot make an unpaid
 * payment look paid.
 */
@Component({
  selector: 'app-payment-result',
  standalone: true,
  imports: [CommonModule, ButtonModule, ProgressSpinnerModule],
  template: `
    <div class="result-page">
      <div class="result-card" [class.ok]="state() === 'success'" [class.bad]="state() === 'failed'">
        @if (state() === 'checking') {
          <p-progressSpinner styleClass="spin" strokeWidth="4" />
          <h2>Confirming your payment…</h2>
          <p>Checking with the payment gateway. This takes a few seconds — please don't close this page.</p>
        } @else if (state() === 'success') {
          <i class="pi pi-check-circle icon ok"></i>
          <h2>Payment successful</h2>
          <p>
            The premium has been received and the policy issued.
            @if (receiptNo()) { Receipt <strong>{{ receiptNo() }}</strong>. }
          </p>
          <div class="actions">
            @if (paymentId()) {
              <p-button label="View receipt" icon="pi pi-receipt" (onClick)="openReceipt()" />
            }
            @if (memberId()) {
              <p-button label="Certificate" icon="pi pi-id-card" [outlined]="true" (onClick)="openCertificate()" />
            }
            <p-button label="Done" icon="pi pi-check" [text]="true" (onClick)="done()" />
          </div>
        } @else if (state() === 'failed') {
          <i class="pi pi-times-circle icon bad"></i>
          <h2>Payment not completed</h2>
          <p>{{ message() || 'The gateway did not confirm this payment. No premium has been collected.' }}</p>
          <div class="actions">
            <p-button label="Try again" icon="pi pi-refresh" (onClick)="done()" />
            @if (paymentId()) {
              <p-button label="Payment details" icon="pi pi-search" [outlined]="true" (onClick)="openDetail()" />
            }
          </div>
        } @else {
          <i class="pi pi-question-circle icon"></i>
          <h2>We couldn't find that payment</h2>
          <p>
            The reference in this link doesn't match any payment we hold. If money left your account,
            don't pay again — check the payment list or contact support with the reference below.
          </p>
          @if (ref()) { <code>{{ ref() }}</code> }
          <div class="actions"><p-button label="Go back" icon="pi pi-arrow-left" (onClick)="done()" /></div>
        }
      </div>
    </div>
  `,
  styles: [`
    .result-page { display: grid; place-items: center; min-height: 60vh; padding: 24px; }
    .result-card {
      background: #fff;
      border: 1px solid var(--line, #e7ecef);
      border-radius: 20px;
      box-shadow: 0 10px 30px rgba(15, 23, 42, 0.07);
      padding: 40px 32px;
      max-width: 520px;
      width: 100%;
      text-align: center;
    }
    .result-card.ok { border-color: #bbf7d0; }
    .result-card.bad { border-color: #fecaca; }

    .icon { font-size: 46px; display: block; margin-bottom: 14px; color: var(--muted, #6b7280); }
    .icon.ok { color: #16a34a; }
    .icon.bad { color: #dc2626; }

    h2 { font-size: 20px; font-weight: 700; margin: 0 0 8px; color: var(--text, #1f2937); }
    p { font-size: 13.5px; color: var(--muted, #6b7280); margin: 0 auto; max-width: 400px; line-height: 1.6; }
    code {
      display: inline-block; margin-top: 12px; padding: 6px 10px; border-radius: 8px;
      background: #f3f4f6; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12.5px;
    }
    .actions { display: flex; gap: 10px; justify-content: center; flex-wrap: wrap; margin-top: 22px; }
    :host ::ng-deep .spin { width: 46px; height: 46px; margin-bottom: 14px; }
  `],
})
export class PaymentResult implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private payments = inject(PaymentService);
  private auth = inject(AuthService);
  private platformId = inject(PLATFORM_ID);
  readonly pageTitle = inject(PageTitleService).title;

  readonly state = signal<'checking' | 'success' | 'failed' | 'unknown'>('checking');
  readonly ref = signal<string>('');
  readonly paymentId = signal<number | null>(null);
  readonly memberId = signal<number | null>(null);
  readonly receiptNo = signal<string | null>(null);
  readonly message = signal<string | null>(null);

  /** A gateway can report success a moment before the confirmation lands. Give it a few tries. */
  private static readonly MAX_ATTEMPTS = 4;
  private attempt = 0;

  ngOnInit() {
    if (!isPlatformBrowser(this.platformId)) return;

    const q = this.route.snapshot.queryParamMap;
    this.ref.set(q.get('ref') ?? '');
    this.paymentId.set(Number(q.get('paymentId')) || null);
    this.memberId.set(Number(q.get('memberId')) || null);

    if (!this.ref()) {
      this.state.set('unknown');
      return;
    }
    this.check();
  }

  /**
   * Re-read the payment. The GET itself asks the gateway again when the row is still pending, so
   * a single call already self-heals; the retries are only for the confirmation arriving late.
   */
  private check() {
    this.attempt++;
    this.payments.payGetByUid(this.ref()).subscribe({
      next: (res: any) => {
        const p = res?.data;
        if (!p) {
          this.state.set('unknown');
          return;
        }

        this.paymentId.set(p.id ?? this.paymentId());
        this.memberId.set(p.memberId ?? this.memberId());
        this.receiptNo.set(p.paymentUniqueId ?? null);

        const status = String(p.paymentStatus || '').toUpperCase();
        if (status === 'SUCCESS') {
          this.state.set('success');
          return;
        }

        // Still in flight — a gateway confirmation can land a moment after the customer does.
        if ((status === 'PENDING' || status === 'INITIATED') && this.attempt < PaymentResult.MAX_ATTEMPTS) {
          setTimeout(() => this.check(), 2500);
          return;
        }

        this.message.set(p.failureReason ?? null);
        this.state.set('failed');
      },
      error: () => {
        if (this.attempt < PaymentResult.MAX_ATTEMPTS) {
          setTimeout(() => this.check(), 2500);
          return;
        }
        this.state.set('unknown');
      },
    });
  }

  openReceipt() {
    this.router.navigate(['/payments/success', this.paymentId()], {
      queryParams: this.memberId() ? { memberId: this.memberId() } : {},
    });
  }

  openCertificate() {
    this.router.navigate(['/policies/certificate', this.memberId()]);
  }

  openDetail() {
    this.router.navigate(['/payments/detail', this.paymentId()]);
  }

  done() {
    this.router.navigate([this.auth.isAdmin() ? '/payments/list' : '/partner-dashboard']);
  }
}
